import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { apiClient } from "../lib/api";

export default function SymbolAutocomplete({ value, onChange, onSelect, placeholder = "Search NSE/BSE…", testId = "symbol-autocomplete" }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get("/prices/search", { params: { q } });
        setResults(data.results || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const choose = (item) => {
    onChange(item.symbol);
    onSelect?.(item);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      choose(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="flex items-center border border-border bg-card focus-within:border-zinc-900">
        <Search className="h-3.5 w-3.5 ml-2.5 text-muted-foreground/70" strokeWidth={1.5} />
        <input
          data-testid={`${testId}-input`}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="flex-1 px-2 py-2 text-sm bg-transparent focus:outline-none font-mono uppercase"
        />
        {loading ? <span className="mr-2 text-[10px] text-muted-foreground/70">…</span> : null}
      </div>
      {open && results.length > 0 ? (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border shadow-sm max-h-72 overflow-auto">
          {results.map((r, idx) => (
            <button
              key={r.symbol + idx}
              type="button"
              data-testid={`${testId}-option-${r.symbol}`}
              onClick={() => choose(r)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                idx === highlight ? "bg-muted" : "hover:bg-secondary"
              }`}
            >
              <div className="min-w-0">
                <div className="font-mono text-sm">{r.symbol}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.name}</div>
              </div>
              <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider whitespace-nowrap">
                {r.exchange}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
